import { glideHomeTo, homeMotionSpan } from './homeMotion.js';

export const homeProjectStops = [.12, .495, .87];

export function projectSnapTarget(stops, position, gestureStart) {
  // Entry and exit remain ordinary page scrolling.
  if (position <= stops[0] || position >= stops.at(-1)) return null;
  const nearest = value => stops.reduce((best, stop, i) =>
    Math.abs(stop - value) < Math.abs(stops[best] - value) ? i : best, 0);
  let index = nearest(position);
  const startIndex = nearest(gestureStart);
  const delta = position - gestureStart;
  // A small deliberate wheel gesture should advance, not repeatedly pull the
  // reader back to the same project. Tiny trackpad drift still settles nearby.
  if (Math.abs(stops[startIndex] - gestureStart) < 4 && Math.abs(delta) >= 24 && index === startIndex) {
    index = Math.max(0, Math.min(stops.length - 1, index + Math.sign(delta)));
  }
  return Math.abs(stops[index] - position) < 1 ? null : stops[index];
}

export function watchHomeProjectSnap(section, viewport, enabled) {
  let timer, cancelGlide, start = null, touching = false;
  let width = window.innerWidth, height = viewport.clientHeight;
  let lastY = window.scrollY, lastScroll = performance.now(), velocity = 0;
  const keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', ' '];
  function cancel() {
    clearTimeout(timer);
    cancelGlide?.();
    cancelGlide = null;
    start = null;
  }
  function available(event) {
    return enabled() && !document.querySelector('dialog[open]') &&
      !event?.target?.closest('input,textarea,select,[contenteditable="true"],.mono-answer-scroll') &&
      !(event?.type === 'keydown' && event.target?.closest('button,a'));
  }
  function input(event) {
    if (event.type === 'keydown' && !keys.includes(event.key)) return;
    if (!available(event)) { cancel(); return; }
    cancelGlide?.();
    cancelGlide = null;
    if (start === null) {
      start = window.scrollY;
      lastY = start; lastScroll = performance.now(); velocity = 0;
    }
    schedule();
  }
  function settle() {
    if (start === null || touching || !available()) return;
    const top = section.getBoundingClientRect().top + window.scrollY;
    const span = homeMotionSpan(section, viewport.clientHeight);
    const destination = projectSnapTarget(homeProjectStops.map(p => top + span * p), window.scrollY, start);
    start = null;
    if (destination !== null) {
      const duration = Math.min(420, 240 + Math.abs(destination - window.scrollY) * .16);
      cancelGlide = glideHomeTo(destination, duration, viewport, velocity);
    }
  }
  function schedule() {
    clearTimeout(timer);
    if (start !== null && !touching) timer = setTimeout(settle, 65);
  }
  function scroll() {
    if (start === null) return;
    const now = performance.now(), elapsed = now - lastScroll;
    if (elapsed > 0) velocity = (window.scrollY - lastY) / Math.max(8, elapsed);
    lastY = window.scrollY; lastScroll = now;
    schedule();
  }
  function touchStart() { cancel(); touching = true; }
  function touchEnd() { touching = false; schedule(); }
  function resize() {
    if (width === window.innerWidth && height === viewport.clientHeight) return;
    width = window.innerWidth; height = viewport.clientHeight; cancel();
  }
  const events = { wheel: input, keydown: input, touchstart: touchStart, touchmove: input,
    touchend: touchEnd, touchcancel: touchEnd, scroll, pointerdown: cancel,
    resize, hashchange: cancel, pagehide: cancel };
  for (const [name, handler] of Object.entries(events)) window.addEventListener(name, handler, { passive: true });
  return () => {
    cancel();
    for (const [name, handler] of Object.entries(events)) window.removeEventListener(name, handler);
  };
}
